import { each, isDate, isNaN, isString, partial } from 'lodash';
import { AppClockService, getDateString, hasNonExpandedYear, WallClockDateService } from '@mews/framework/datetime';
import { setEndDate, setStartDate } from '../actions/dates';
import { showDates, showProperties, showSpaceCategories } from '../actions/navigation';
import { isAppInitialized } from '../modules/app/selectors';
import { isMultiHotel } from '../modules/configuration/selectors';
import { hasCity } from '../modules/entities/cities/selectors';
import { getEnterprise } from '../modules/entities/enterprises/selectors';
import { closeApp, disableTracking, enableTracking, openApp } from '../modules/global/actions';
import { setCurrency } from '../modules/global/currencyCode';
import { checkLanguageConfiguration } from '../modules/global/languageCode';
import { getValidCurrencyCode } from '../modules/globalization/selectors';
import { setInitialAdultCount, setInitialChildCount } from '../modules/occupancy/reducer';
import { isValidOccupancyCount } from '../modules/occupancy/utils';
import { selectCity, selectHotel, setVoucherCode, trySelectRoom } from '../modules/resourceOrder/actions';
import { getParsedEndDate, getParsedStartDate } from '../modules/resourceOrder/selectors';
import { getMinimalEndDate, getMinimalStartDate } from '../selectors/views/dates';
import { initializeDatesOccupancies } from '../views/dates/actions';
import { calendarDatesChanged } from '../views/dates/slices/calendar';
import { logError } from './logError';

const SHOWHOTELS_ERROR = 'showHotels method is supported only in multi hotel mode.';
const SETCITY_ERROR = 'setCity method is supported only in multi hotel mode.';

interface Options {
    appClockService: AppClockService;
}

export function createApiObject(store: $TSFixMe, { appClockService }: Options) {
    const { dispatch, getState } = store;
    let apiCallQue: $TSFixMe = [];
    let isInitialized = false;
    const unsubscribe = store.subscribe(() => {
        if (!isInitialized && isAppInitialized(getState())) {
            isInitialized = true;
            unsubscribe();
            flushApiCallQue();
        }
    });

    const setStartDateApi = checkDate((dateString: $TSFixMe) => {
        dispatch(calendarDatesChanged());
        // if the date change happens on different step, the app will be in inconsistent state
        dispatch(showDates());

        const state = getState();
        const prevStartDate = getParsedStartDate(state);

        const parsedStartDate = WallClockDateService.parse(dateString);
        const nowUtc = appClockService.getAppNowUtc();
        const minStartDate = getMinimalStartDate(state, { nowUtc });
        const nextStartDate = WallClockDateService.max(parsedStartDate, minStartDate);
        dispatch(setStartDate(nextStartDate.value));

        // keep interval between dates
        const endDate = getParsedEndDate(state);
        if (endDate != null && prevStartDate != null && WallClockDateService.isLessOrEqual(endDate, nextStartDate)) {
            const dayInterval = WallClockDateService.diffDays(endDate, prevStartDate);
            const minEndDate = getMinimalEndDate(state, { nowUtc });
            const nextEndDate = WallClockDateService.max(
                WallClockDateService.addDays(nextStartDate, dayInterval),
                minEndDate
            );
            dispatch(setEndDate(nextEndDate.value));
        }
    });
    const setEndDateApi = checkDate((dateString: $TSFixMe) => {
        dispatch(calendarDatesChanged());
        // if the date change happens on different step, the app will be in inconsistent state
        dispatch(showDates());

        const state = getState();
        const prevEndDate = getParsedEndDate(state);

        const parsedEndDate = WallClockDateService.parse(dateString);
        const nowUtc = appClockService.getAppNowUtc();
        const minEndDate = getMinimalEndDate(state, { nowUtc });
        const nextEndDate = WallClockDateService.max(parsedEndDate, minEndDate);
        dispatch(setEndDate(nextEndDate.value));

        const startDate = getParsedStartDate(state);
        // @ts-expect-error $TSFixMeError: Argument of type 'WallClockDate | null' is not assignable to parameter of type 'WallClockDate'.
        if (WallClockDateService.isLessOrEqual(nextEndDate, startDate)) {
            // @ts-expect-error $TSFixMeError: Argument of type 'WallClockDate | null' is not assignable to parameter of type 'WallClockDate'.
            const dayInterval = WallClockDateService.diffDays(startDate, prevEndDate);
            const minStartDate = getMinimalStartDate(state, { nowUtc });
            const nextStartDate = WallClockDateService.max(
                WallClockDateService.addDays(nextEndDate, dayInterval),
                minStartDate
            );
            dispatch(setStartDate(nextStartDate.value));
        }
    });
    const setVoucherCodeApi = (voucherCode: $TSFixMe) =>
        checkVoucher(voucherCode, () => {
            dispatch(showDates());
            dispatch(setVoucherCode(voucherCode));
        });
    const showRoomsApi = (enterpriseId: $TSFixMe) => {
        const enterprise = getEnterprise(getState(), { enterpriseId });

        if (enterprise) {
            // if the date change happens on different step, the app will be in inconsistent state
            dispatch(showDates());
            dispatch(selectCity(enterprise.cityId));
            dispatch(selectHotel(enterprise.id));
        }
    };

    const api = {
        open: () => dispatch(openApp()),
        close: () => dispatch(closeApp()),
        enableTracking: () => dispatch(enableTracking()),
        disableTracking: () => dispatch(disableTracking()),
        setStartDate: (date: $TSFixMe) => executeApiCall(partial(setStartDateApi, date)),
        setEndDate: (date: $TSFixMe) => executeApiCall(partial(setEndDateApi, date)),
        setAdultCount: (adultCount: $TSFixMe) => {
            if (isValidOccupancyCount(adultCount)) {
                dispatch(setInitialAdultCount(adultCount));
                dispatch(initializeDatesOccupancies());
            } else {
                logError(`AdultCount "${adultCount}" is not valid occupancy number`);
            }
        },
        setChildCount: (childCount: $TSFixMe) => {
            if (isValidOccupancyCount(childCount)) {
                dispatch(setInitialChildCount(childCount));
                dispatch(initializeDatesOccupancies());
            } else {
                logError(`ChildCount "${childCount}" is not valid occupancy number`);
            }
        },
        setVoucherCode: (voucherCode: string) => executeApiCall(partial(setVoucherCodeApi, voucherCode)),
        setLanguageCode: (languageCode: string) =>
            dispatch(checkLanguageConfiguration(languageCode, { isClientApiCall: true })),
        setCurrencyCode: (currencyCode: string) =>
            executeApiCall(() => {
                const validatedCurrency = getValidCurrencyCode(getState(), { currencyCode });
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (validatedCurrency) {
                    dispatch(setCurrency({ currencyCode: validatedCurrency }));
                } else {
                    logError(`Currency "${currencyCode}" is not found.`);
                }
            }),
        setRooms: () =>
            executeApiCall(() => {
                logError(
                    'setRooms method is deprecated. Use category mappings in Booking Engine configuration instead.'
                );
            }),
        setStepRooms: (startDate = null, endDate = null, voucherCode = null) => {
            if (!isMultiHotel(getState())) {
                return executeApiCall(() => {
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (startDate) {
                        setStartDateApi(startDate);
                    }
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (endDate) {
                        setEndDateApi(endDate);
                    }
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (voucherCode) {
                        setVoucherCodeApi(voucherCode);
                    }
                    dispatch(showSpaceCategories());
                });
            }

            logError('setStepRooms method is supported only in single hotel mode.');
        },
        setStepRates: (roomId: string, startDate = null, endDate = null, voucherCode = null) => {
            if (!isMultiHotel(getState())) {
                return executeApiCall(() => {
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (startDate) {
                        setStartDateApi(startDate);
                    }
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (endDate) {
                        setEndDateApi(endDate);
                    }
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (voucherCode) {
                        setVoucherCodeApi(voucherCode);
                    }
                    dispatch(trySelectRoom(roomId));
                });
            }

            logError('setStepRates method is supported only in single hotel mode.');
        },
        showRooms: (hotelId: string) =>
            executeApiCall(() => {
                if (!isMultiHotel(getState())) {
                    return dispatch(showSpaceCategories());
                }

                return showRoomsApi(hotelId);
            }),
        showRates: (roomId: string) => {
            if (!isMultiHotel(getState())) {
                return executeApiCall(() => dispatch(trySelectRoom(roomId)));
            }

            logError('showRates method is supported only in single hotel mode.');
        },
        setCity: (cityId: string) => {
            if (!isMultiHotel(getState())) {
                logError(SETCITY_ERROR);
            }

            return executeApiCall(() => {
                if (hasCity(getState(), { cityId })) {
                    // if the date change happens on different step, the app will be in inconsistent state
                    dispatch(showDates());
                    dispatch(selectCity(cityId));
                }
            });
        },
        showHotels: () => {
            if (!isMultiHotel(getState())) {
                logError(SHOWHOTELS_ERROR);
            }

            return executeApiCall(() => dispatch(showProperties()));
        },
    };

    return api;

    function flushApiCallQue() {
        each(apiCallQue, (c) => c());
        apiCallQue = [];
    }

    function executeApiCall(call: $TSFixMe) {
        if (!isInitialized) {
            apiCallQue.push(call);
        } else {
            call();
        }
    }
}

const checkDate = (setter: $TSFixMe) => (date: $TSFixMe) => {
    if (!isDate(date) || isNaN(date.valueOf())) {
        logError('Expected the date to be a Date instance.');
        return;
    }
    if (!hasNonExpandedYear(date)) {
        logError(`Expected the date year to be in range 0-9999. Year: ${date.getFullYear()}`);
        return;
    }

    const dateString = getDateString(date);
    setter(dateString);
};

function checkVoucher(voucher: string, setter: $TSFixMe) {
    if (isString(voucher)) {
        setter(voucher);
    } else {
        logError('Expected the voucher to be a String.');
    }
}
